import React, { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { QuestionMarkCircleIcon } from "@heroicons/react/24/outline";

/**
 * InfoTooltip - A hoverable info icon that shows explanatory text.
 * Uses portal to render tooltip at document body level to avoid overflow issues.
 */
export function InfoTooltip({ text, size = 12 }) {
  const [isVisible, setIsVisible] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const iconRef = useRef(null);

  useEffect(() => {
    if (isVisible && iconRef.current) {
      const rect = iconRef.current.getBoundingClientRect();
      const tooltipMaxWidth = 200;
      const tooltipHeight = 50;

      // Position above the icon by default
      let top = rect.top - tooltipHeight - 6;
      let left = rect.left + rect.width / 2 - tooltipMaxWidth / 2;

      // If too close to top, show below
      if (top < 8) {
        top = rect.bottom + 6;
      }

      // Keep within horizontal viewport
      if (left < 8) left = 8;
      if (left + tooltipMaxWidth > window.innerWidth - 8) {
        left = window.innerWidth - tooltipMaxWidth - 8;
      }

      setPosition({ top, left });
    }
  }, [isVisible]);

  return (
    <>
      <span
        className="info-tooltip-trigger"
        onMouseEnter={() => setIsVisible(true)}
        onMouseLeave={() => setIsVisible(false)}
        ref={iconRef}
      >
        <QuestionMarkCircleIcon className={`h-${Math.floor(size/4)} w-${Math.floor(size/4)}`} style={{ width: size, height: size }} />
      </span>
      {isVisible && createPortal(
        <div
          className="info-tooltip-popup"
          style={{ top: position.top, left: position.left }}
        >
          {text}
        </div>,
        document.body
      )}
    </>
  );
}

/**
 * CyclesExplainer - A dismissible banner explaining what "cycles" mean.
 * Shows once per session, can be dismissed.
 */
export function CyclesExplainer({ currentCycle, onDismiss }) {
  const [dismissed, setDismissed] = useState(() => {
    return sessionStorage.getItem("cyclesExplainerDismissed") === "true";
  });

  const handleDismiss = () => {
    setDismissed(true);
    sessionStorage.setItem("cyclesExplainerDismissed", "true");
    onDismiss?.();
  };

  if (dismissed) return null;

  return (
    <div className="cycles-explainer">
      <div className="explainer-content">
        <QuestionMarkCircleIcon className="h-3.5 w-3.5" />
        <span>
          <strong>What's a cycle?</strong> One cycle = one flight operation.
          Current: <strong>{currentCycle || "—"}</strong> cycles.
        </span>
      </div>
      <button className="explainer-dismiss" onClick={handleDismiss} title="Got it">
        ✕
      </button>
    </div>
  );
}
